import { type Observable } from 'rxjs';

import {
  type SearchAndFiltersProps,
  type StyleConfigProps,
  type VisibilityElementProps,
} from '@blaze-runtime/engine-domain';
import {
  type BorderRadiusValue,
  type CanvasElementPropertiesWithData,
  type ElementLabelProps,
  type ElementTitleStyleProps,
  type PropertiesConfigInputColorModel,
  type PropertiesConfigInputColumnModel,
  type PropertiesConfigInputContainerItemActionModel,
  type PropertiesConfigInputListCustomFieldModel,
  type PropertiesConfigInputSpacingAndAlignmentModel,
  type PropertiesConfigInputVisualEffectModel,
} from '@blaze-shared/elements-domain';
import { type DatasetColumn } from '@blaze-shared/schema-domain';
import { type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';

export interface MapCoords {
  lat: number;
  lng: number;
}

export interface MapMarker {
  id: string;
  position: MapCoords;
  item: Record<string, unknown>;
  fields: {
    title?: DatasetColumn;
    field?: DatasetColumn;
    image?: DatasetColumn;
    customFields$?: Observable<PropertiesConfigInputListCustomFieldModel[] | undefined>;
    card?: MapCard;
  };
}

export interface MapCard {
  title?: DatasetColumn;
  description?: DatasetColumn;
  image?: DatasetColumn;
  customFields$?: Observable<PropertiesConfigInputListCustomFieldModel[] | undefined>;
  actions$?: Observable<PropertiesConfigInputContainerItemActionModel[]>;
}

export enum MarkerStyle {
  Pin = 'pin',
  Bubble = 'bubble',
  Image = 'image',
}

export enum MapType {
  Road = 'roadmap',
  Satellite = 'satellite',
  Hybrid = 'hybrid',
  Terrain = 'terrain',
}

export enum MapStyle {
  Light = 'light',
  Dark = 'dark',
}

export enum InitialMapLocation {
  Selection = 'selection',
  Current = 'current',
}

export interface MapSettingsInfo {
  apiKey: string;
  maps: MapStyleInfo[];
}

export interface MapStyleInfo {
  id: string;
  styleId: string;
}

export interface MapMarkerStyles {
  default: {
    fill: PropertiesConfigInputColorModel;
    outline: PropertiesConfigInputColorModel;
    label: PropertiesConfigInputColorModel;
    labelBackground: PropertiesConfigInputColorModel;
  };
  selected: {
    fill: PropertiesConfigInputColorModel;
    outline: PropertiesConfigInputColorModel;
    label: PropertiesConfigInputColorModel;
    labelBackground: PropertiesConfigInputColorModel;
  };
}

export interface MapCardStyles {
  title: PropertiesConfigInputColorModel;
  description: PropertiesConfigInputColorModel;
  links: PropertiesConfigInputColorModel;
  background: PropertiesConfigInputColorModel;
  divider: PropertiesConfigInputColorModel;
  buttonFill: PropertiesConfigInputColorModel;
  buttonLabel: PropertiesConfigInputColorModel;
  radius: BorderRadiusValue;
  effect: PropertiesConfigInputVisualEffectModel;
}

export interface MapStyles {
  title: PropertiesConfigInputColorModel;
  background: PropertiesConfigInputColorModel;
  radius: BorderRadiusValue;
  offset: PropertiesConfigInputSpacingAndAlignmentModel;
  effect: PropertiesConfigInputVisualEffectModel;
}

export const DEFAULT_MAP_COORDS = {
  lat: 40.0,
  lng: -100.0,
};

export type MapFieldType = 'card' | 'marker';

export type MapPropsModel = CanvasElementPropertiesWithData &
  VisibilityElementProps &
  SearchAndFiltersProps &
  StyleConfigProps & {
    content: {
      columns: PropertiesConfigInputColumnModel[];
      title: ElementLabelProps;
      input: {
        coordinates?: string;
      };
      options: {
        type: MapType;
        style: MapStyle;
        enableZoom: boolean;
        showAllMarkers: boolean;
      };
      location: {
        initial: {
          type: InitialMapLocation;
          coords?: { lat: number; lng: number };
        };
        zoom: number;
      };
      markers: {
        style: MarkerStyle;
        title?: string;
        field?: string;
        image?: string;
        customFields: PropertiesConfigInputListCustomFieldModel[];
        clustering: boolean;
      };
      card: {
        enabled: boolean;
        title?: string;
        description?: string;
        image?: string;
        customFields: PropertiesConfigInputListCustomFieldModel[];
        actions: PropertiesConfigInputContainerItemActionModel[];
      };
    };
    style: {
      map: MapStyles;
      titleBlock: ElementTitleStyleProps;
      marker: MapMarkerStyles & {
        selectedStyle: 'default' | 'selected';
      };
      card: MapCardStyles;
      container: {
        outline: PropertiesConfigInputColorModel;
        outlineWidth: ValueWithUnitOfMeasure;
      };
    };
  };
